// Task API service — CRUD operations for employee tasks

import { apiRequest } from './api';

// ── Types ────────────────────────────────────────────────────

export type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high';

export type TaskUserRef = {
    id: string;
    first_name: string;
    last_name: string | null;
};

export type TaskApiRecord = {
    id: string;
    organization_id?: string;
    title: string;
    description: string | null;
    status: TaskStatus;
    priority: TaskPriority;
    due_date: string;
    completion_comment: string | null;
    completed_at: string | null;
    created_at: string;
    updated_at: string;
    assigned_by: TaskUserRef | null;
    assigned_to: TaskUserRef | null;
    // Legacy alias retained for components that referenced `assignee`
    assignee?: TaskUserRef;
};

export type TaskListMeta = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
};

export type TaskListResponse = {
    success: boolean;
    message: string;
    data: TaskApiRecord[];
    meta: TaskListMeta;
};

export type CreateTaskPayload = {
    title: string;
    description: string;
    assigned_to: string;
    status: TaskStatus;
    priority: TaskPriority;
    due_date: string;
};

export type FetchTasksParams = {
    page?: number;
    perPage?: number;
    assigned_to?: string;
    status?: TaskStatus | '';
    priority?: TaskPriority | '';
    title?: string;
};

// ── API Functions ────────────────────────────────────────────

export async function fetchTasks(params: FetchTasksParams = {}): Promise<TaskListResponse> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.perPage) query.set('per_page', String(params.perPage));
    if (params.assigned_to) query.set('assigned_to', params.assigned_to);
    if (params.status) query.set('status', params.status);
    if (params.priority) query.set('priority', params.priority);
    if (params.title) query.set('title', params.title);

    const qs = query.toString();
    const res = await apiRequest<TaskApiRecord[]>(`/api/tasks${qs ? `?${qs}` : ''}`);
    return res as unknown as TaskListResponse;
}

export async function createTask(payload: CreateTaskPayload): Promise<TaskApiRecord> {
    const res = await apiRequest<TaskApiRecord>('/api/tasks', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}

export async function deleteTask(id: string): Promise<void> {
    await apiRequest(`/api/tasks/${id}`, { method: 'DELETE' });
}
